// 中文文案。Shape 由 en.ts 决定（Dict），缺键或多键都会编译报错。
// 兜底名与文案池遵循设计文档 §4.6：不许出现「玩家 1234」「暂无数据」。

import type { Dict } from './en';

export const zh: Dict = {
  'app.title': '打败我',
  'app.loading': '赛道铺设中…',
  'app.error': '没连上赛道。看看网络，再试一次。',
  'app.retryBoot': '再试一次',

  // --- 开场浮层（设计 §4.1/§4.2）---
  // 冷读测试：说明混在战绩里被当成一句怪话读掉 — 战绩归战绩，操作归操作。
  'intro.link': '{name} 跑了 {n} 米',
  'intro.first': '{name} 跑了 {n} 米',
  'intro.walkin': '前一位路人：{name}，跑了 {n} 米',
  'intro.rerun': '你的最好成绩 {n} 米',
  'intro.tap': '点屏幕任意位置开始',
  'intro.hintJump': '点一下跳',
  'intro.hintSlide': '按住下滑，滑铲过杆',
  'intro.hintLane': '你在下面那条道',
  'intro.hintGhost': '上面是TA的鬼影，你在下面',
  'intro.flagged': '这条成绩存疑，换官方鬼影陪你跑',

  // --- HUD ---
  'hud.meters': '米',
  'hud.ahead': '领先',
  'hud.behind': '落后',
  'lane.you': '你',
  'mascot.name': '小跑',
  'lane.pastYou': '刚才的你',
  'lane.weeklyYou': '本周的你',
  'ownchain.banner': '跑赢刚才的你，新链就从你开始',

  // --- 中断与倒数（设计 §4.5）---
  'pause.title': '已暂停',
  'pause.body': '你离开了一下，从停下的地方接着跑。',
  'countdown.go': '跑',

  // --- 新手免费复活（设计 §2.6）---
  'revive.title': '免费复活一次',
  'revive.body': '只有第一局有，不用看广告。',
  'revive.accept': '原地爬起来（{s}）',
  'revive.decline': '算了',

  // --- 广告复活（设计 §8.1）---
  // 只在广告已经就位时出现（§4.6），所以文案可以直说，不用留退路。
  'revive.ad.title': '看个广告，原地复活',
  'revive.ad.accept': '看广告复活（{s}）',

  // --- 停帧教学（设计 §2.3 教学约束，v0.5.2）---
  // 画面已经停在障碍前，文案要同时说清「做什么动作」和「对着谁做」。
  'tutorial.jump': '点一下，跳过它',
  'tutorial.slide': '按住往下滑，钻过去',
  'tutorial.again': '下一局重新教学',
  'tutorial.armed': '已开启',

  // --- 情绪节点（设计 §2.5）---
  'beat.overtake': '你超过了{name}',
  'beat.ghostDead': '{name} 死在这',
  'marker.revive': '续',

  // --- 决胜一刻（§4bis.22）---
  // 超越之后 HUD 差距表换装成领先计数；印章下方是离开的路标。
  'hud.lead': '领先 +{d} 米',
  'peak.dismiss': '点其他地方看成绩',

  // --- 所见即判定（§4bis.23）---
  // 死亡定格上的两框说明——是解释，不是文案池：一把钥匙只配一句话。
  'hud.deathBox': '你的判定框，碰到了它的',

  // --- 快斗（§4bis.28，§4bis.30 改写）---
  // 链接打开的是一段 12 秒切片，切片的终点是对方倒下的地方再往前一秒——
  // 但那是「线」，不是「局的结束」。越过线之后，局继续，直到你自己死。
  // 所以 12 秒是买路，不是时长：按钮说 12 秒买到什么，副标题说之后会发生什么。
  // 之前写成「应战 · 12 秒」，读起来像承诺一局只有 12 秒——那是不诚实的。
  'duel.cta': '应战 · 12 秒接手',
  'duel.ctaSub': '越过他倒下的地方，然后能跑多远跑多远',
  'duel.full': '从头跑全程',
  'duel.banner': '{name} 在 {d} 米倒下',
  'duel.aim': '先活过他倒下的地方 · 之后是你自己的路',
  'duel.ghostMark': '{name} 到此',
  'duel.teach': '点一下跳 · 下滑蹲',
  'duel.label': '12 秒快斗',
  'duel.retry': '再来 · 这局不算',
  // 越线之后 HUD 差距表的第三种模式：前面是这条链上没人到过的赛道。
  'hud.territory': '你的地盘 +{d} 米',

  // --- 帮我：第二个分享动词（§4bis.31，迭代 006）---
  //
  // 「打败我」是挑衅，需要一个高光时刻才说得出口；「帮我」是托付，
  // 每一次死亡都说得出口，而且它的数字永远是正的——你加了多少米。
  // 所以这一组文案的规矩只有一条：**说你做到了什么，绝不说你没做到什么**。
  // 按钮点的是一个「人」，给的是一个「礼物」，不是一句认输。
  //
  // 时机：种子埋在越线的封印上（画布，不可点，不打断跑动），
  // 选择做在死亡页上。原因很实在——在封印那一刻你能交出去的是 +0 米，
  // 礼物还不存在；你多跑一米，这份礼物就重一分。
  'duel.sealPlant': '跑得越远，交回去的越多',
  'handoff.cta': '交回给 {name} · 你加了 {n} 米',
  // 全程跑的死亡页上没有「上一个人」——这条命是你自己起的头，所以是「交出去」，
  // 不是「交回去」。用 handoff.cta 会渲染成「交回给 刚才的你」，那是错的。
  'handoff.ctaOwn': '把这条命交出去 · 你跑了 {n} 米',
  'handoff.ctaOwnSub': '让朋友从你倒下的地方接着跑',
  'handoff.label': '接力',
  // 收到一条被交回来的命：{name} 交给你的人，{p} 这条命现在的米数，{s} 你接手的米数。
  'handoff.open': '{name} 把这条命交给你了',
  // 只说「你从哪儿开跑」。这条命现在多少米，标题（openOther）或卡片已经说过了，
  // 在同一屏上说第二遍是把真正干活的那个数字（{s}）淹掉。
  'handoff.openSub': '从 {s} 米接着跑',
  'handoff.openCta': '接住',
  // 第三个人打开同一条链接：链接是开放的，卡片才是指名的。没人被锁住。
  'handoff.openOther': '这条命现在在 {p} 米',
  'handoff.openOtherCta': '接着跑',
  // 接手页的脚注，写给**正在看这一屏的人**。
  // 之前这里放的是 handoff.ctaSub「他从你倒下的地方接着跑」——那句是写给
  // 分享者的（「你」= 倒下的人），放在接手页上等于对一个还没跑的人说「你倒下的
  // 地方」。收件人根本没倒在任何地方。
  'handoff.openNote': '你往前跑的每一米，都算进这条命里',
  'handoff.finish': '{k} 个人，一条命，跑穿了 {p} 米',
  // 分享卡：说的是「这条命」的米数，和「我」加的米数——两个不同的数字，
  // 因为 score_m 不是任何一个人的成绩（§4bis.31 §5）。
  'handoff.life': '这条命跑到 {p} 米',

  // --- 结果页（设计 §4.4）---
  'result.distance': '{m} 米',
  'result.deltaSeconds': '再坚持 {s} 秒就赢了',
  'result.opponentLine': '{name}：{n} 米',
  'result.finished': '你把整条赛道跑完了，这种人不多。',
  'result.best': '你的最好成绩 {n} 米',
  'result.newBest': '新纪录，上次最好 {n} 米',
  'result.submitting': '正在核对这一局…',
  'result.offline': '网络断了，成绩先存在本地，回来了自动补交。',
  'result.rejected': '服务端没能核对上这一局。',
  'result.unsaved': '服务器暂时收不下这一局，成绩先存着，下次打开自动补交。',

  'btn.sendBack': '发回去让TA看看',
  'btn.challengeOthers': '再挑战别人',
  'btn.retry': '再来一次',
  'btn.passOn': '先传给别人',
  'btn.findSomeone': '找人来打',
  'btn.judge': '拉个人来做裁判',
  'btn.ownChain': '开一条自己的链',
  'btn.share': '发给TA',
  'btn.close': '关掉',

  // --- 对手阶梯（§4bis.21）：三个都打得过的对手，一键再来 ---
  // 全程没有奖励：阶梯上唯一的奖品就是对手本人。
  'ladder.title': '下一个打谁',
  'ladder.rung.opponent': '刚才的对手',
  'ladder.rung.self': '本周的你',
  'ladder.rung.daily': '今日路人',
  'ladder.again': '再赢一次',
  'ladder.gap': '差 {d} 米',
  'ladder.selfEmpty': '先留下一个成绩',
  'ladder.line': '{name} · {n} 米',
  'ladder.score': '{n} 米',
  // 第二级正是刚跑完的这一局：说清楚它是热的，别让人以为是旧纪录（H02 B3）。
  'ladder.selfJustSet': '刚跑出的 {n} 米',
  'ladder.selfBeat': '超过它',
  'ladder.selfBanner': '前面跑的是本周最好的你',
  'ladder.dailyBanner': '今天的对手：{name}',

  // --- 链条排行榜（设计 §5.3）---
  'chain.title': '这条链',
  'chain.you': '你',
  'chain.summary': '这条链已传 {size} 人，你第 {rank}',
  'chain.pair': '目前就你俩，拉个第三个人来打破僵局',
  'chain.empty': '拉一个人进来，这条链就从你开始',

  // --- 昵称（设计 §4.6）---
  'nickname.title': '给自己起个名',
  'nickname.placeholder': '你的名字',
  'nickname.reroll': '换一个',
  'nickname.save': '保存',
  'nickname.saved': '已保存',
  'nickname.error': '这个名字不行，换一个试试。',

  // --- 分享 ---
  'share.copied': '链接已复制',
  'share.failed': '没复制上，链接就在地址栏里',

  'feedback.link': '说句话',
  'privacy.link': '隐私',

  // --- 文案池（设计 §4.6），每局按种子随机取一条 ---
  'pool.lose': [
    '差 {s} 秒就赢了',
    '再多一跳就过去了',
    '你死的地方，{name} 也差点死',
    '{name} 在前面 {d} 米笑了一下',
    '就差 {d} 米，一步的事',
    '{name} 还在前面等着，别让TA等太久',
  ],
  'pool.win': [
    '{m} 米，{name} 被我超了',
    '{name}，该你了',
    '刚刚超过 {name} {d} 米，不多，就一点',
    '{name} 的数字，现在归你了',
    '你从 {name} 手里多抢了 {d} 米',
  ],
  'pool.tie': [
    '你俩死在同一米，这是缘分',
    '都是 {m} 米，谁也没赢谁',
    '打平了，再跑一局分胜负',
    '同一米，同一秒，重来',
    '和 {name} 完全打平，这很少见',
  ],
  // 注意：默认对手既可能是吉祥物小跑，也可能是真人路人（设计 §3.2）——
  // 文案不能把对手当成非人（「它」「去找个真人」都会冒犯到路人）。
  'pool.defaultWin': [
    '你超过了{name}',
    '{name} 被你甩了 {d} 米',
    '{name} 追不上了，找个朋友来试试',
    '{m} 米，{name} 没跟上',
    '{name} 落后 {d} 米，回头看了你一眼',
  ],
  'pool.defaultLose': [
    '{name} 还在前面',
    '{name} 领先你 {d} 米',
    '再坚持 {s} 秒就追上 {name} 了',
    '还差点，{name} 停在 {n} 米',
    '{name} 跑了 {n} 米，你跑了 {m} 米',
  ],
  'pool.rerun': [
    '你的最好成绩 {m} 米',
    '{m} 米，自己的数字自己破',
    '这一局跑了 {m} 米',
  ],
  'pool.shareText': [
    '我跑了 {m} 米，来打败我',
    '{m} 米，该你了',
    '同一条赛道，我 {m} 米，你试试',
    '{m} 米，死得很蠢，你来',
    '我在这条赛道跑到 {m} 米，看你能不能多跑一米',
  ],

  // --- 印章文案池（§4bis.22）。大字：数字来吹牛，对手永远是个人；
  // 行长超限时由 src/peak.ts 的钳制规则缩一号。 ---
  'pool.peakStampWin': [
    // 参数：name、d（领先）、m
    '赢了{name}，多跑 {d} 米',
    '{name} 已倒，你还在跑',
    '{name} 的纪录，到此为止',
    '这 {d} 米，是从 {name} 手里抢的',
    '{m} 米。{name}，看清楚了',
  ],
  'pool.peakStampNear': [
    // 参数：s、name
    '差 {s} 秒，就差一口气',
    '{s} 秒，一跳的事',
    '{name} 差点就守不住了',
    '再撑 {s} 秒，赢的就是你',
  ],
  'pool.peakCtaBack': [
    // 赢 + 链接进入：趁热发回去
    '就现在，发回去给{name}',
    '让{name}亲眼看看',
    '趁热，甩回去',
    '给{name}回个信：你输了',
  ],
  'pool.peakCtaFresh': [
    // 赢 + 路人/首局/重跑
    '趁热，拉个人进来',
    '这成绩，值得找个对手',
    '发出去，看谁敢接',
    '找个人，让TA也跑一遍',
  ],
  'pool.peakCtaNear': [
    // 险败 + 真实发件人；{name} = 对手昵称
    '告诉{name}：就差 {s} 秒',
    '发回去，下一局没这么险',
    '让{name}看看你追得多近',
    '差 {s} 秒，{name}该紧张了',
  ],
  'pool.peakCtaNearWalkin': [
    // 险败 + 路人/官方鬼影——没有可以回信的人（DR-R1）
    '就差 {s} 秒，找个人替你报仇',
    '这局太险了，值得让人看看',
    '发出去，看谁能跑过这一局',
    '差一口气，让朋友来补上',
  ],

  // --- 快斗文案池（§4bis.28）。参数：{name} 发件人、{d} 他的米数、
  // {p} 你的米数、{n} 米数差（输了是差多少，赢了是多出多少，§4bis.30）。
  // 中文是主声音，英文另写，不是翻译。---
  'pool.duelWin': ['活过了', '越过他的坑', '他的终点，你的中途', '线是你的了'],
  // §4bis.30：赢局的收据带上真实的多出米数——004 的每个赢家都只会说「多活一秒」，
  // 这是第一次两个赢家的卡片会说不一样的话。
  'pool.duelWinLine': [
    '他倒在 {d} 米。你跑到 {p} 米，多出 {n} 米。',
    '{d} 米是他的极限。你把线推到了 {p} 米。',
    '他没站住的地方，你站住了——还往前跑了 {n} 米。',
  ],
  'pool.duelTie': ['同归', '一起倒的', '同一步'],
  'pool.duelTieLine': ['你死在他倒下的那一步。', '同一个坑，同一秒，谁也没赢。'],
  'pool.duelLose': ['还差 {n} 米', '这次没过去', '他守住了'],
  'pool.duelLoseLine': ['他到 {d} 米，你到 {p} 米。', '离他倒下的地方，还差 {n} 米。'],
  'pool.duelInstant': ['起手就没了', '这局还没开始就结束了', '他可是跑到了 {d} 米。'],
  'pool.duelCta': [
    '回敬一局',
    '把这口气还回去',
    '换你来接这一坑',
    '把线推到 {p} 米了，换他来',
  ],

  // --- 帮我的三个随机池（§4bis.31）---
  // 和上面共用同一条 mulberry32(runSeed ^ 0x51ab5ea1) 流，所以基线稳定。
  // {d} 他倒下的米数、{p} 这条命现在的米数、{n} 我加的米数、{name} 上一个人。
  // **{n} 是这一池的不变量，不是某一句碰巧带上的修饰。**
  // 一条命有好几个作者，所以每个作者都得有一句「哪一段是我的」——这正是从
  // 接力争锋里挖出来的那条「每一棒都要有自己的最高分」。分享卡是真正会走进
  // 聊天窗口的那个东西；如果三句里只有一句带数字，那么大约三分之二的传播里，
  // 「我加了多少」这件事根本不存在。语气随便变，数字不能掉。
  'pool.handoffCard': [
    '{name} 死在 {d} 米。我帮他多跑了 {n} 米。',
    '我又往前带了 {n} 米，这条命现在 {p} 米，该你了',
    '我加了 {n} 米。接住，别让它停在我这儿',
  ],
  // 全程跑分享出去的第一跳：这条命还没有别人的份，所以一个 {name} 都不能出现。
  // （用 pool.handoffCard 会渲染成「刚才的你 死在 0 米」——源局根本不存在。）
  // 同一条不变量。全程跑起头的那一跳没有「上一个人」，所以你加的那一段
  // 就是全部——{n} 与 {p} 同值，但用 {n} 说，口径才和上面那一池一致。
  'pool.handoffCardOwn': [
    '我跑出了 {n} 米。接着跑，别让它停在这儿。',
    '这条命我起了个头，{n} 米，该你了',
    '我先跑了 {n} 米。接住，看你能把它带多远',
  ],
  // 死亡页的印章，当这一局可以交出去的时候（§4bis.31）。
  //
  // 默认池里有「你从 {name} 手里多抢了 {d} 米」——**抢**。把那句盖在一屏
  // 主按钮是「交回给他」的页面上，是自相矛盾：同一屏同时说「我抢了你的」和
  // 「这个送你」。帮我的规矩只有一条——说你做到了什么，而你做到的是**添**，
  // 不是**夺**。参数只用 {n}（你加的米数）和 {m}（这条命现在的米数），
  // 因为 titleFor 与 duelParams 里的 {d}/{n} 含义并不相同。
  // **印章的字数是有上限的**，这一池必须和它替换掉的那一池同一个体例。
  // 「活过了」是 3 个字，「线是你的了」是 5 个——印章是印章，收据在下面那一行。
  // 第一版写成了收据长度（11–19 字），在 390px 上折成两行，把整屏往下推了
  // ~90px，「给自己起个名」的输入框被挤出了屏幕底边。名字在这个游戏里是承重的：
  // 每一张卡片、每一个鬼影标签、每一句「交回给 …」都在叫一个人的名字，而这恰好
  // 是那块屏幕。数字不在印章上，它在下面那行和按钮上（都已经有了）。
  'pool.handoffStamp': [
    '这条命更远了',
    '你这一段跑完了',
    '又往前推了一截',
    '该下一个人了',
  ],
  'pool.handoffResult': [
    '又往前推了 {n} 米',
    '这条命现在 {p} 米，还没停',
    '你替他多走了 {n} 米',
  ],
};
